import { useMatch, useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import DummyProfile from '@/assets/images/dummy_profile.png';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useMe } from '@/api-client/use-me';
import { useLogout } from '@/api-client/use-logout';
import { useAuthStore } from '@/state/store/auth';
import { useTheme } from '@/styles/theme/theme-provider';

interface MobileAccountMenuProps {
  onNavigate?: () => void;
}

export function MobileAccountMenu({ onNavigate }: MobileAccountMenuProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { logout, platformRole, activeOrgRole } = useAuthStore();
  const { Logout } = useLogout();
  const { data, isLoading } = useMe();

  const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim();
  const isAdminRoute = useMatch({ path: '/admin/*' });
  const currentRole = isAdminRoute ? platformRole : activeOrgRole;
  let translatedRole = '';
  if (currentRole?.includes('sys')) {
    const roleKey = currentRole?.replace('sys_', '') || 'member';
    translatedRole = t('ADMIN_ROLES_' + roleKey.toUpperCase());
  } else {
    const roleKey = currentRole?.replace('org_', '') || 'member';
    translatedRole = t('ORGTEAM_ROLES_' + roleKey.toUpperCase());
  }

  const go = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handleSignout = async () => {
    try {
      await Logout();
      logout();
      navigate('/login');
      onNavigate?.();
    } catch {
      /* empty */
    }
  };

  const rowClass =
    'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted transition-colors';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3 rounded-xl px-4 py-3 bg-muted/50">
        <div className="relative overflow-hidden rounded-full border border-border h-10 w-10 shrink-0">
          {isLoading ? (
            <Skeleton className="h-10 w-10 rounded-full" />
          ) : (
            <img
              src={data?.profileImageUrl || DummyProfile}
              alt="profile"
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {isLoading ? (
            <Skeleton className="w-24 h-4 mb-1" />
          ) : (
            <p className="text-sm font-semibold text-high-emphasis truncate capitalize">
              {fullName || data?.email || ''}
            </p>
          )}
          <p className="text-xs text-low-emphasis capitalize">{translatedRole}</p>
        </div>
      </div>

      <button type="button" className={rowClass} onClick={() => go('/profile')}>
        <User className="h-5 w-5 text-medium-emphasis" />
        <span>{t('MY_PROFILE')}</span>
      </button>

      <button
        type="button"
        className={rowClass}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        {theme === 'dark' ? (
          <Sun className="h-5 w-5 text-medium-emphasis" />
        ) : (
          <Moon className="h-5 w-5 text-medium-emphasis" />
        )}
        <span>{t('COMMON_THEME')}</span>
        <span className="ml-auto text-xs text-low-emphasis capitalize">{theme}</span>
      </button>

      <button type="button" className={rowClass} onClick={handleSignout}>
        <LogOut className="h-5 w-5 text-destructive" />
        <span className="text-destructive">{t('LOG_OUT')}</span>
      </button>
    </div>
  );
}
